import { FC, Fragment, ReactNode, useState } from 'react';
import Markdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';

import { Banner, BodyText, Card, Tab, TabGroup } from '@servicetitan/design-system';

import {
    JsonSchema,
    JsonSchemaDiscriminator,
    JsonSchemaObject,
    JsonSchemaOrRef,
    JsonSchemaPrimitive,
} from '../../api/documentation.api';
import { flattenObjectSchema } from '../utils';
import * as Styles from './docs.module.scss';

const EnumTable: FC<{ enumSchema: JsonSchemaPrimitive }> = ({ enumSchema }) => {
    const enumNames = enumSchema['x-enumNames'];
    const items = enumSchema.enum!.map((value, index) => ({
        name: enumNames ? enumNames[index] : value,
        value,
    }));
    const includeNames = enumNames !== undefined && items.some(item => item.name !== item.value);
    return (
        <table className={Styles.simple}>
            <thead>
                <tr>
                    {includeNames && <th>Enum Name</th>}
                    <th>Enum Value</th>
                </tr>
            </thead>
            <tbody>
                {items.map(item => (
                    <tr key={item.name}>
                        {includeNames && (
                            <td>
                                <code>{item.name}</code>
                            </td>
                        )}
                        <td>
                            <code>{item.value}</code>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const DiscriminatorMappingTable: FC<{
    discriminator: JsonSchemaDiscriminator;
    formatSchemaType: (schema: JsonSchemaOrRef) => ReactNode;
}> = ({ discriminator, formatSchemaType }) => {
    return (
        <Fragment>
            <Banner className="m-t-1" icon status="info" title="Polymorphic Type">
                <BodyText>
                    The schema of this object varies depending on the value of the{' '}
                    <code>{discriminator.propertyName}</code> discriminator property.
                </BodyText>
            </Banner>
            <table className={Styles.simple}>
                <thead>
                    <tr>
                        <th>
                            <code>{discriminator.propertyName}</code> Value
                        </th>
                        <th>Schema Type</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(discriminator.mapping).map(
                        ([discriminatorValue, discriminatorRef]) => (
                            <tr key={discriminatorValue}>
                                <td>
                                    <code>{JSON.stringify(discriminatorValue)}</code>
                                </td>
                                <td>{formatSchemaType({ $ref: discriminatorRef })}</td>
                            </tr>
                        ),
                    )}
                </tbody>
            </table>
        </Fragment>
    );
};

const ObjectPropertyTable: FC<{
    objectSchema: JsonSchemaObject;
    formatSchemaType: (schema: JsonSchemaOrRef) => ReactNode;
}> = ({ objectSchema, formatSchemaType }) => {
    return (
        <table className={Styles.simple}>
            <thead>
                <tr>
                    <th className={Styles.propertyName}>Property Name</th>
                    <th className={Styles.required}>Required</th>
                    <th className={Styles.schemaType}>Type</th>
                    <th>Description</th>
                </tr>
            </thead>
            <tbody>
                {Object.entries(objectSchema.properties!).map(([name, propertySchema]) => (
                    <tr key={name}>
                        <td>
                            <code>{name}</code>
                        </td>
                        <td>{objectSchema.required?.includes(name) ? 'Yes' : 'No'}</td>
                        <td>{formatSchemaType(propertySchema)}</td>
                        <td className={Styles.description}>
                            {'description' in propertySchema && (
                                <Markdown>{propertySchema.description}</Markdown>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const ObjectSchemaDetails: FC<{
    objectSchema: JsonSchemaObject;
    formatSchemaType: (schema: JsonSchemaOrRef) => ReactNode;
}> = ({ objectSchema, formatSchemaType }) => {
    const { discriminator } = objectSchema;
    return discriminator ? (
        <DiscriminatorMappingTable
            discriminator={discriminator}
            formatSchemaType={formatSchemaType}
        />
    ) : (
        <ObjectPropertyTable objectSchema={objectSchema} formatSchemaType={formatSchemaType} />
    );
};

export const SchemaDetails: FC<{
    name: string;
    schema: JsonSchema;
    formatSchemaType: (schema: JsonSchemaOrRef) => ReactNode;
    resolveSchemaRef: (ref: string) => JsonSchema;
}> = ({ name, schema, formatSchemaType, resolveSchemaRef }) => {
    const [activeView, setActiveView] = useState<'Table' | 'JSON'>('Table');
    return (
        <Card elevation="base" padding="thin">
            <Card.Section>
                <TabGroup>
                    {(['Table', 'JSON'] as const).map(view => (
                        <Tab
                            key={view}
                            active={view === activeView}
                            onClick={() => {
                                setActiveView(view);
                            }}
                        >
                            {`${view} View`}
                        </Tab>
                    ))}
                </TabGroup>
            </Card.Section>
            <Card.Section className="p-0-i">
                {activeView === 'Table' ? (
                    'allOf' in schema ? (
                        <ObjectPropertyTable
                            objectSchema={flattenObjectSchema(
                                name,
                                schema.allOf!,
                                resolveSchemaRef,
                            )}
                            formatSchemaType={formatSchemaType}
                        />
                    ) : 'enum' in schema ? (
                        <EnumTable enumSchema={schema} />
                    ) : schema.type === 'object' ? (
                        <ObjectSchemaDetails
                            objectSchema={schema}
                            formatSchemaType={formatSchemaType}
                        />
                    ) : null
                ) : (
                    <SyntaxHighlighter language="json">
                        {JSON.stringify(schema, null, 2)}
                    </SyntaxHighlighter>
                )}
            </Card.Section>
        </Card>
    );
};
SchemaDetails.displayName = 'SchemaDetails';
